import {
  postComplianceDocumentsUploadAbandon,
  postComplianceDocumentsUploadComplete,
  postMarketingAssetsUploadAbandon,
  postMarketingAssetsUploadComplete,
} from '@/lib/api';
import type { UploadAssetDomain } from '@/lib/uploadAssetDomain';

export type UploadFinalizer = (
  fileIds: number[],
  crc32ByFileId?: Record<number, string>,
) => Promise<void>;
type UploadAbandoner = (fileIds: number[]) => Promise<void>;

const completeParams = (
  fileIds: number[],
  crc32ByFileId?: Record<number, string>,
) =>
  crc32ByFileId && Object.keys(crc32ByFileId).length > 0
    ? { file_ids: fileIds, crc32: crc32ByFileId }
    : { file_ids: fileIds };

const uploadFinalizers: Partial<
  Record<UploadAssetDomain, UploadFinalizer>
> = {
  compliance_documents: async (fileIds, crc32ByFileId) => {
    await postComplianceDocumentsUploadComplete(
      completeParams(fileIds, crc32ByFileId),
    );
  },
  marketing_assets: async (fileIds, crc32ByFileId) => {
    await postMarketingAssetsUploadComplete(
      completeParams(fileIds, crc32ByFileId),
    );
  },
};

const uploadAbandoners: Partial<
  Record<UploadAssetDomain, UploadAbandoner>
> = {
  compliance_documents: async (fileIds) => {
    await postComplianceDocumentsUploadAbandon({ file_ids: fileIds });
  },
  marketing_assets: async (fileIds) => {
    await postMarketingAssetsUploadAbandon({ file_ids: fileIds });
  },
};

export function finalizeUpload(
  domain: UploadAssetDomain,
  fileIds: number[],
  crc32ByFileId?: Record<number, string>,
): Promise<void> {
  const finalizer = uploadFinalizers[domain];
  if (!finalizer) {
    return Promise.reject(
      new Error(`No upload finalizer configured for ${domain}`),
    );
  }
  return finalizer(fileIds, crc32ByFileId);
}

/**
 * Releases the server-side reservations of a batch nobody will finish uploading. Without it the
 * rows sit in `pending_upload`, holding their storage paths until they age out, and the same
 * filenames cannot be uploaded again in the meantime.
 */
export function abandonUpload(
  domain: UploadAssetDomain,
  fileIds: number[],
): Promise<void> {
  const abandoner = uploadAbandoners[domain];
  if (!abandoner || fileIds.length === 0) {
    return Promise.resolve();
  }
  return abandoner(fileIds);
}
